// A bottom sheet for the phone board. Local on purpose: the touch-kit PR brings
// a shared BottomSheet, and this is the only file the board's sheets depend on,
// so swapping it is one file. It keeps to that component's shape: a grabber, a
// title row with a close button, and a body that scrolls.

import type { ReactNode } from 'react';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { SAFE_AREA_LEFT, SAFE_AREA_RIGHT } from '../../../layout/chrome';
import { CloseIcon } from './icons';
import { ink, surface } from './surface';

export interface LocalSheetProps {
    open: boolean;
    onClose: () => void;
    title: string;
    subtitle?: string;
    /** Names the dialog for assistive technology. Defaults to the title. */
    label?: string;
    /**
     * Opened on top of another sheet: the page is already dimmed, so this one
     * does not dim it again (two 45% layers read as 70%). Its backdrop is still
     * there to catch a tap outside the sheet.
     */
    nested?: boolean;
    children: ReactNode;
}

export function LocalSheet({
    open,
    onClose,
    title,
    subtitle,
    label,
    nested = false,
    children,
}: LocalSheetProps) {
    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            BackdropProps={
                nested
                    ? { invisible: true }
                    : { sx: { backgroundColor: 'rgba(0, 0, 0, 0.45)' } }
            }
            PaperProps={{
                role: 'dialog',
                'aria-label': label ?? title,
                sx: theme => ({
                    bgcolor: surface(theme),
                    backgroundImage: 'none',
                    borderRadius: '16px 16px 0 0',
                    pt: 1,
                    pb: 'max(24px, env(safe-area-inset-bottom, 0px))',
                    pl: SAFE_AREA_LEFT,
                    pr: SAFE_AREA_RIGHT,
                    maxHeight: '85vh',
                    '@supports (height: 100dvh)': { maxHeight: '85dvh' },
                    display: 'flex',
                    flexDirection: 'column',
                }),
            }}
        >
            <Box
                aria-hidden="true"
                sx={theme => ({
                    width: 36,
                    height: 4,
                    borderRadius: '2px',
                    bgcolor:
                        theme.palette.mode === 'light' ? '#c4c4c4' : '#555',
                    alignSelf: 'center',
                    mb: '10px',
                    flexShrink: 0,
                })}
            />
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pl: '20px',
                    pr: 1,
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                    }}
                >
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: 17,
                            fontWeight: 500,
                            lineHeight: 'normal',
                        }}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography
                            sx={theme => ({
                                fontSize: 13,
                                lineHeight: 'normal',
                                color: ink(theme, 0.6),
                            })}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <IconButton
                    aria-label="Close"
                    onClick={onClose}
                    sx={theme => ({
                        width: 44,
                        height: 44,
                        p: 0,
                        color: ink(theme, 0.6),
                    })}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', mt: 1 }}>
                {children}
            </Box>
        </Drawer>
    );
}
